'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';

import { Container } from '@/components/ui/Container';
import {
  GLOSSARY_CATEGORIES,
  GLOSSARY_PAGE,
  type GlossaryCategoryId,
  type GlossaryTerm,
} from '@/content/glossary';
import { cn } from '@/lib/utils';
import { Eyebrow } from '@/components/ui/Eyebrow';

interface GlossaryBrowserProps {
  terms: readonly GlossaryTerm[];
}

/** Terms starting with a digit ("3PL") bucket here rather than inventing a "3" heading. */
const NUMERIC_GROUP = '#';

type CategoryFilter = GlossaryCategoryId | 'all';

const CATEGORY_LABELS = new Map<GlossaryCategoryId, string>(
  GLOSSARY_CATEGORIES.map((category) => [category.id, category.label]),
);

function groupKey(term: GlossaryTerm): string {
  const first = term.term.charAt(0).toUpperCase();
  return first >= 'A' && first <= 'Z' ? first : NUMERIC_GROUP;
}

/** Match against the term, its synonyms, and the definition body. */
function matchesQuery(term: GlossaryTerm, query: string): boolean {
  if (!query) return true;
  const haystack = [term.term, ...(term.aka ?? []), term.definition]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

/**
 * The /glossary term browser: a search box, category chips, and an A–Z row,
 * filtering a definition list of every term.
 *
 * This is a client component, but every term is present in the server-rendered
 * HTML — the filters start empty, and filtering only hides nodes that are
 * already in the DOM. Nothing is fetched, and no filter state reaches the URL,
 * so there are no thin `?letter=`-style variants for a crawler to find.
 *
 * The glossary is a navigational island: `noindex, follow` on the hub and every
 * term page, absent from the sitemap and the footer, so the only way in is a
 * direct URL — but once you are inside, every path is live. `follow` is what
 * makes that legal. Do not add links to /glossary from site navigation without
 * revisiting the indexing decision in
 * docs/superpowers/specs/2026-08-11-glossary-articles-design.md.
 */
export function GlossaryBrowser({ terms }: GlossaryBrowserProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [letter, setLetter] = useState<string | null>(null);

  const normalizedQuery = query.trim().toLowerCase();

  const sorted = useMemo(
    () =>
      [...terms].sort((a, b) =>
        a.term.localeCompare(b.term, 'en', { sensitivity: 'base' }),
      ),
    [terms],
  );

  // Everything except the letter filter — drives both which letters are
  // selectable and (once the letter filter is applied) the visible list.
  const preLetter = useMemo(
    () =>
      sorted.filter(
        (term) =>
          (category === 'all' || term.category === category) &&
          matchesQuery(term, normalizedQuery),
      ),
    [sorted, category, normalizedQuery],
  );

  const availableLetters = useMemo(
    () => new Set(preLetter.map(groupKey)),
    [preLetter],
  );

  const visible = useMemo(
    () => (letter ? preLetter.filter((term) => groupKey(term) === letter) : preLetter),
    [preLetter, letter],
  );

  const groups = useMemo(() => {
    const map = new Map<string, GlossaryTerm[]>();
    for (const term of visible) {
      const key = groupKey(term);
      const bucket = map.get(key);
      if (bucket) bucket.push(term);
      else map.set(key, [term]);
    }
    // '#' sorts before 'A' so numeric terms lead, as in a printed glossary.
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [visible]);

  const letterRow = useMemo(
    () => [
      NUMERIC_GROUP,
      ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
    ],
    [],
  );

  const isFiltered = Boolean(normalizedQuery) || category !== 'all' || letter !== null;

  const clearFilters = useCallback(() => {
    setQuery('');
    setCategory('all');
    setLetter(null);
  }, []);

  /**
   * The whole row opens the term page, which is what a reader expects from a
   * card-shaped thing. The term title is a real <Link>, so this handler only
   * covers the dead space around it — and bows out whenever the click meant
   * something else: another anchor, a modified click, or the end of a text
   * selection. Without the selection check, copying a definition would
   * navigate away instead.
   */
  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, slug: string) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if ((event.target as HTMLElement).closest('a, button, input, label')) return;
      if (window.getSelection()?.toString()) return;
      router.push(`/glossary/${slug}`);
    },
    [router],
  );

  const termsById = useMemo(
    () => new Map(terms.map((term) => [term.slug, term.term])),
    [terms],
  );

  return (
    <section
      aria-label="Glossary terms"
      className="bg-[#f2f2f2] pb-20 pt-10 text-[#121212] md:pb-24 lg:pb-32"
    >
      {/* Filter bar — sticky on desktop only; on mobile it would eat the viewport. */}
      <div className="z-30 bg-[#f2f2f2] lg:sticky lg:top-0 lg:pb-6 lg:pt-8">
        <Container>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <label className="flex w-full items-center gap-3 border-[0.5px] border-border bg-white px-5 py-3.5 lg:max-w-[420px]">
                <span className="sr-only">{GLOSSARY_PAGE.searchLabel}</span>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  className="size-[18px] shrink-0 text-[#121212]/40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="9" cy="9" r="6" />
                  <path d="m13.5 13.5 3.5 3.5" strokeLinecap="round" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={GLOSSARY_PAGE.searchPlaceholder}
                  className="w-full bg-transparent font-light text-[15px] tracking-[-0.02em] text-[#121212] outline-none placeholder:text-[#121212]/40 lg:text-[16px]"
                />
              </label>

              <p aria-live="polite" className="font-light text-[15px] text-[#121212]/60">
                {visible.length === terms.length
                  ? `${terms.length} terms`
                  : `${visible.length} of ${terms.length} terms`}
              </p>
            </div>

            {/* Category chips — the /blog tab bar: green dot + underlined label. */}
            <nav aria-label="Filter terms by category">
              <ul className="flex items-stretch overflow-x-auto border-[0.5px] border-border scrollbar-hide">
                {[
                  { id: 'all' as const, label: GLOSSARY_PAGE.allCategoriesLabel },
                  ...GLOSSARY_CATEGORIES,
                ].map((chip) => {
                  const active = category === chip.id;
                  return (
                    <li key={chip.id} className="shrink-0">
                      <button
                        type="button"
                        onClick={() => setCategory(chip.id)}
                        aria-pressed={active}
                        className={cn(
                          // cursor-pointer is explicit: Tailwind 4 leaves buttons
                          // on the browser default of `cursor: default`.
                          'flex h-[52px] cursor-pointer items-center justify-center gap-2 px-6 text-[14px] uppercase tracking-[-0.02em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset lg:h-[64px] lg:px-[38px] lg:text-[17px]',
                          active
                            ? 'bg-accent text-[#121212]'
                            : 'text-[#121212] hover:bg-[#121212]/[0.04]',
                        )}
                      >
                        <span className="whitespace-nowrap underline underline-offset-4">
                          {chip.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* A–Z row. Letters with no match under the current search/category
                are disabled rather than hidden, so the row never reflows. */}
            <nav aria-label="Filter terms by first letter">
              <ul className="flex flex-wrap gap-1">
                {letterRow.map((char) => {
                  const enabled = availableLetters.has(char);
                  const active = letter === char;
                  return (
                    <li key={char}>
                      <button
                        type="button"
                        disabled={!enabled}
                        aria-pressed={active}
                        onClick={() => setLetter(active ? null : char)}
                        className={cn(
                          'flex size-8 items-center justify-center border-[0.5px] text-[14px] uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:size-9 lg:text-[15px]',
                          enabled && 'cursor-pointer',
                          active && 'border-accent bg-accent text-[#121212]',
                          !active &&
                            enabled &&
                            'border-border text-[#121212] hover:border-[#121212]',
                          !enabled && 'cursor-not-allowed border-border/40 text-[#121212]/25',
                        )}
                      >
                        {char}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </Container>
      </div>

      <Container className="mt-12 lg:mt-14">
        {groups.length === 0 ? (
          <div className="border-[0.5px] border-border px-6 py-16 text-center lg:py-24">
            <p className="font-light text-[22px] tracking-[-0.02em] lg:text-[28px]">
              {GLOSSARY_PAGE.emptyHeading}
            </p>
            <p className="mx-auto mt-3 max-w-[420px] font-light text-[15px] text-[#121212]/60 lg:text-[16px]">
              {GLOSSARY_PAGE.emptyBody}
            </p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-7 inline-flex h-[46px] cursor-pointer items-center border-[0.5px] border-[#121212] px-6 text-[14px] uppercase tracking-[-0.02em] transition-colors hover:bg-[#121212] hover:text-[#f2f2f2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {GLOSSARY_PAGE.clearLabel}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-16 lg:gap-20">
            {groups.map(([groupLetter, groupTerms]) => (
              <section key={groupLetter} aria-labelledby={`letter-${groupLetter}`}>
                <h2
                  id={`letter-${groupLetter}`}
                  className="border-b border-[#121212]/15 pb-4 font-light leading-none text-[40px] text-accent-ink lg:text-[56px] tracking-[-0.022em] lg:tracking-[-0.026em]"
                >
                  {groupLetter}
                </h2>

                <dl className="mt-2">
                  {groupTerms.map((term) => {
                    const related = term.related ?? [];
                    return (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- the term title inside is a real link; adding a second tab stop here would only duplicate it
                    <div
                      key={term.slug}
                      onClick={(event) => handleRowClick(event, term.slug)}
                      className="grid scroll-mt-28 cursor-pointer grid-cols-1 gap-x-10 gap-y-3 border-b border-[#121212]/10 py-8 lg:grid-cols-[300px_minmax(0,1fr)] lg:scroll-mt-[190px] lg:py-10"
                    >
                      <dt>
                        <dfn
                          id={term.slug}
                          className="block font-normal not-italic leading-tight tracking-[-0.02em] text-[22px] text-[#121212] lg:text-[26px]"
                        >
                          <Link
                            href={`/glossary/${term.slug}`}
                            className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            {term.term}
                          </Link>
                        </dfn>

                        {term.aka && term.aka.length > 0 && (
                          <p className="mt-1.5 font-light text-[14px] text-[#121212]/50 lg:text-[15px]">
                            {term.aka.join(' · ')}
                          </p>
                        )}

                        <Eyebrow tone="light" className="mt-3">
                          {CATEGORY_LABELS.get(term.category)}
                        </Eyebrow>
                      </dt>

                      <dd className="lg:pt-1">
                        <p className="font-light leading-[1.45] tracking-[-0.01em] text-[16px] text-[#121212]/85 lg:text-[18px]">
                          {term.definition}
                        </p>

                        {/* Visible affordance for the row link — the only one
                            on touch, where there is no hover. Hidden from
                            assistive tech: the term title above already links
                            here, and two links to one page read as noise. */}
                        <Link
                          href={`/glossary/${term.slug}`}
                          aria-hidden="true"
                          tabIndex={-1}
                          className="mt-4 inline-flex items-center gap-1.5 font-normal uppercase tracking-[-0.02em] text-[13px] text-accent-ink transition-opacity hover:opacity-70 lg:text-[14px]"
                        >
                          {/* Underline the words only. On the flex container it
                              would run under the arrow too, and the gap would
                              split it into two segments. */}
                          <span className="underline underline-offset-4">Read more</span>
                          <span aria-hidden="true">→</span>
                        </Link>

                        {(related.length > 0 || term.link) && (
                          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
                            {related.length > 0 && (
                              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-light text-[14px] text-[#121212]/55 lg:text-[15px]">
                                <span>{GLOSSARY_PAGE.relatedLabel}:</span>
                                {related.map((slug, index) => (
                                  <span key={slug}>
                                    <Link
                                      href={`/glossary/${slug}`}
                                      className="rounded-sm text-accent-ink underline underline-offset-4 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                    >
                                      {termsById.get(slug) ?? slug}
                                    </Link>
                                    {index < related.length - 1 && (
                                      <span aria-hidden="true">,</span>
                                    )}
                                  </span>
                                ))}
                              </p>
                            )}

                            {term.link && (
                              <Link
                                href={term.link.href}
                                className="inline-flex items-center gap-1.5 rounded-sm font-light text-[14px] text-[#121212] transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:text-[15px]"
                              >
                                <span className="underline underline-offset-4">
                                  {term.link.label}
                                </span>
                                <span aria-hidden="true">→</span>
                              </Link>
                            )}
                          </div>
                        )}
                      </dd>
                    </div>
                    );
                  })}
                </dl>
              </section>
            ))}
          </div>
        )}

        {isFiltered && groups.length > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="mt-12 inline-flex h-[46px] cursor-pointer items-center border-[0.5px] border-[#121212] px-6 text-[14px] uppercase tracking-[-0.02em] transition-colors hover:bg-[#121212] hover:text-[#f2f2f2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {GLOSSARY_PAGE.clearLabel}
          </button>
        )}
      </Container>
    </section>
  );
}
